<template>
  <Transition name="inno-subcategories--slide">
    <VcScrollbar
      v-if="item && item.childCategories?.length"
      class="inno-subcategories"
      role="menubar"
      tabindex="-1"
      vertical
      :aria-label="ariaLabel"
      @onkeydown.enter="scheduleHide"
      @blur="scheduleHide"
      @mouseleave="scheduleHide"
      @focus="cancelHide"
      @mouseenter="cancelHide"
    >
      <ul class="inno-subcategories__list" role="none">
        <div class="font-semibold ml-3 mb-2" v-if="item.level > 0">{{ item.name }}</div>
        <template v-for="(child, index) in items" :key="child.id">
          <VcMenuItem
            :id="`subcategory-${child.id}`"
            class="inno-subcategories__item"
            size="sm"
            color="secondary"
            max-lines="2"
            role="menuitem"
            :to="routes[child.id]"
            :active="child.isActive"
            @keyup.arrow-right="focusMenuItem(child.childCategories[0].id)"
            @keyup.arrow-left="focusMenuItem(item.id)"
            @keyup.arrow-up="focusMenuItem(item.childCategories[index - 1].id)"
            @keyup.arrow-down="focusMenuItem(item.childCategories[index + 1].id)"
            @click="$emit('close')"
            @focusin="showChildren(child)"
            @mouseover="showChildren(child)"
          >
            <div class="flex items-center">
              <template v-if="child.level === 1">
                <VcImage class="w-10 2xl:w-12" :src="child.imgSrc" :alt="child.name" lazy />
              </template>
              <span :class="{ 'font-semibold': child.level === 1 }">{{ child.name }}</span>
            </div>

            <!-- Hide arrows because we only want to show three levels -->
            <template v-if="child.childCategories?.length && child.level < 3" #append>
              <VcIcon class="inno-subcategories__arrow" name="chevron-right-small" :size="12" />
            </template>
          </VcMenuItem>
          <div
            class="inno-subcategories__highlight"
            :class="[child.level === 1 ? 'left-[710px]' : 'left-[350px]']"
            v-if="child.name === activeItem?.name && child.megaMenuUrl && child.megaMenuImage?.url"
          >
            <a class="inno-subcategories__highlight--item" :href="child.megaMenuUrl.value as string">
              <img class="inno-subcategories__highlight--image" :src="child.megaMenuImage?.url" />
            </a>
          </div>
        </template>
      </ul>
    </VcScrollbar>
  </Transition>

  <InnoSubcategories
    v-if="activeItem"
    :item="activeItem"
    :on-schedule-hide="scheduleHide"
    :on-cancel-hide="cancelHide"
    :aria-label="$t('shared.layout.header.mega_menu.aria_labels.subcategories', { category: activeItem.name })"
    @close="$emit('close')"
  />
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from "vue";
import { useCategoriesRoutes } from "@/core/composables";
import type { IMarkedCategory } from "@/core/types";

interface IEmits {
  (e: "close"): void;
}

interface IProps {
  item: IMarkedCategory;
  onCancelHide?: () => void;
  onScheduleHide?: () => void;
  ariaLabel?: string;
}

defineEmits<IEmits>();
const props = defineProps<IProps>();

const activeItem = ref<IMarkedCategory | null>(null);
let timeout: ReturnType<typeof setTimeout> | null = null;
let switchTimeout: ReturnType<typeof setTimeout> | null = null;

const items = computed(() =>
  (props.item.childCategories || []).sort((a, b) => {
    if (a.priority === undefined) {
      return 1;
    }
    if (b.priority === undefined) {
      return -1;
    }
    return a.priority - b.priority;
  }),
);
const routes = useCategoriesRoutes(items);

function showChildren(item: IMarkedCategory) {
  cancelHide();

  if (switchTimeout) {
    clearTimeout(switchTimeout);
  }

  // Show only max 3 levels
  if (activeItem.value?.id === item.id || item.level > 2) {
    return;
  }

  switchTimeout = setTimeout(() => {
    activeItem.value = item;
  }, 200);
}

function scheduleHide() {
  timeout = setTimeout(() => {
    activeItem.value = null;
  }, 300);

  props.onScheduleHide?.();
}

function cancelHide() {
  if (timeout) {
    clearTimeout(timeout);
  }

  if (switchTimeout) {
    clearTimeout(switchTimeout);
  }

  timeout = null;
  switchTimeout = null;

  props.onCancelHide?.();
}

function focusMenuItem(id?: string) {
  if (!id) {
    return;
  }

  const element = document.querySelector(`[id="subcategory-${id}"] > [role="menuitem"]`) as HTMLElement;

  if (element) {
    element.focus();
  }
}

onBeforeUnmount(() => {
  if (timeout) {
    clearTimeout(timeout);
  }

  if (switchTimeout) {
    clearTimeout(switchTimeout);
  }
});
</script>

<style lang="scss">
.inno-subcategories {
  @apply relative overflow-visible !overflow-x-visible px-2.5 border-r border-neutral-200 bg-white mt-2.5 pt-6 pb-3;

  &__highlight {
    width: calc(350px * 2);
    @apply absolute -z-10 top-[0] h-full bg-white;

    &--item {
      @apply bg-neutral-200 rounded-lg absolute flex items-center justify-center w-[300px] top-[50px] h-[425px] right-[20px];
    }

    &--image {
      @apply object-contain;
      height: 90%;
    }
  }

  &__list {
    @apply flex-none;
    width: 340px;
    height: 100%;
  }

  &__item {
    position: relative;
    z-index: 1;
  }

  &__arrow {
    @apply fill-secondary-400;
  }

  &__divider-container {
    @apply flex justify-center w-6 transition-all duration-300 ease-in-out;

    &:last-child {
      @apply w-0;
    }
  }

  .vc-menu-item__inner {
    @apply hover:bg-neutral-100 rounded-md xl:max-h-[55px] 2xl:max-h-[70px];
    flex: 1;
  }
}
</style>
